import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import { Viewer } from '@toast-ui/react-editor'
import '@toast-ui/editor/dist/toastui-editor-viewer.css'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Badge, Button } from '@/components'
import commonUtils from '@/utils/commonUtils'
import useCopyToClipboard from '@/hooks/useCopyToClipboard'
import { useAppSelector } from '@/redux/hooks'
import { UpdateBlog } from './updateBlog'
import {
  buttonName,
  CommentListItem,
  confirmMessages,
  getBadgeLabel,
  getHeaderTitle,
  getMobileBadgeClass,
  maskIpSuffix,
  successMessages,
  withDrawnCumtomer,
} from '@/pages/board/boardItem'
import { BoardHeader } from '@/pages/board/_components/boardHeader'
import { BlogDetailItem } from '../blogItem'
import { Comments } from '../comment/comments'
import { useResponsive } from '@/hooks/useResponsive'

interface CommentData {
  cmpnyCd: string
  nttSn: number
}

const DetailBlog: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()
  const headerTitle = getHeaderTitle(window.location.pathname)
  const [isDisabled, setIsDisabled] = useState(true) // 초기값은 비활성화
  const [isEditMode, setIsEditMode] = useState(false)
  const { showAlert, showConfirm } = useDialog()
  const [searchParams] = useSearchParams() // 쿼리 파라미터 사용
  const [isCopied, copyToClipboard] = useCopyToClipboard()
  const nttSn = Number(searchParams.get('nttSn')) // 게시판 번호
  const cmpnyCd = '001'
  const commentRef = useRef<HTMLDivElement>(null)
  const { user } = useAppSelector((state) => state.auth)
  const [imgUrls, setImgUrls] = useState<string[]>([])
  const [commentList, setCommentList] = useState<CommentListItem[]>([])
  const [commentListCnt, setCommentListCnt] = useState(0)
  const { isMobile, isTablet, isDesktop } = useResponsive()
  const [blogDetail, setBlogDetail] = useState<BlogDetailItem>({
    bbsSj: '',
    nttSn: 0,
    nttSj: '',
    nttCn: '',
    nickName: '',
    withDrawYn: '',
    regId: '',
    regDate: '',
    updId: '',
    updDate: '',
    accessIp: '',
    readngCnt: 0,
    answerCnt: 0,
  })

  useEffect(() => {
    getBlogDetail()
    getCommentList()
  }, [nttSn])

  useEffect(() => {
    window.scrollTo(0, 0) // behavior: 'smooth' 없이 즉시 이동
  }, [])

  const [fileList, setFileList] = useState<
    { fileSeq: number; fileName: string }[]
  >([])
  const withDrawYn = blogDetail?.withDrawYn === 'Y'
  const handleCopy = () => {
    const url = window.location.href // 현재 페이지 URL
    copyToClipboard(url)
  }

  const extractImageUrls = (htmlContent: string) => {
    const imgUrls = Array.from(
      new DOMParser()
        .parseFromString(htmlContent, 'text/html')
        .querySelectorAll('img'),
    ).map((img) => {
      const imgSrc = img.src
      const filePath = imgSrc.split('upload')[1]
      return decodeURIComponent(filePath)
    })
    return imgUrls
  }

  const getBlogDetail = async () => {
    try {
      const response = await axios.get(`/blog/detail`, {
        headers: { 'Cache-Control': 'no-cache' },
        params: { cmpnyCd: cmpnyCd, nttSn },
      })

      const { data } = response.data // 응답에서 data 추출

      // 호출 예시
      const htmlContent = data.blogDetail.nttCn // 서버에서 받은 HTML 콘텐츠
      const imgUrls = extractImageUrls(htmlContent)

      if (!data || !data.blogDetail) {
        return
      }

      const fileInfoList = data.fileList.map((file: any) => ({
        fileName: file.fileNm,
        fileSeq: file.fileSeq,
      }))

      setFileList(fileInfoList)
      setImgUrls(imgUrls)
      setBlogDetail(data.blogDetail)
    } catch (err: any) {
      console.error('Error fetching detailBlog:', err)
    }
  }
  const getCommentList = async () => {
    try {
      const data: CommentData = {
        cmpnyCd: cmpnyCd,
        nttSn: nttSn,
      }

      const response = await axios.get(`/blog/comment/list`, {
        params: { ...data },
      })

      const { commentList } = response.data.data

      if (Array.isArray(commentList)) {
        setCommentList(commentList)
        setCommentListCnt(commentList?.length)
      }
    } catch (err: any) {
      console.error('Error fetching commentList:', err)
    }
  }

  const deleteBlog = async () => {
    try {
      await axios.post(`/blog/delete`, { cmpnyCd: cmpnyCd, nttSn })

      showAlert({
        type: 'success',
        title: successMessages.delete,
        btnName: buttonName.confirm,
        onClose() {
          setTimeout(() => {
            navigate(`/blog`)
          }, 100)
        },
      })
    } catch (err: any) {
      console.error('Error fetching detailBlog:', err)
    }
  }

  const handleModalAction = (type: 'update' | 'delete' | 'cancel') => {
    if (type === 'update') {
      showConfirm({
        title: confirmMessages.update,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          //navigate(`/blog/update?nttSn=${nttSn}`, { state: { blogDetail } })
          setIsEditMode(true)
        },
      })
    }

    if (type === 'delete') {
      showConfirm({
        title: confirmMessages.delete,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          deleteBlog()
        },
      })
    }

    if (type === 'cancel') {
      showConfirm({
        title: confirmMessages.cancel,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          setTimeout(() => {
            navigate(0) // 500ms 후에 페이지 이동
          }, 100)
        },
      })
    }
  }

  const scrollToComment = () => {
    commentRef.current?.scrollIntoView({
      behavior: 'smooth', // 부드러운 스크롤
    })
  }

  return (
    <>
      {!isMobile && <BoardHeader title={headerTitle} />}

      {!isEditMode ? (
        <>
          {!isMobile ? (
            <div className="blogDetailContainer">
              {!isMobile && (
                <Badge color="neverbe">
                  {getBadgeLabel(blogDetail.bbsSj || '기타')}
                </Badge>
              )}
              <div className="blogDetailTitle">{blogDetail.nttSj}</div>

              <div className="blogDetailMeta">
                <div className="blogDetailMetaRow">
                  <div>
                    <div>
                      작성자 :&nbsp;
                      {withDrawYn
                        ? withDrawnCumtomer.name
                        : blogDetail?.nickName}
                      &nbsp;({maskIpSuffix(blogDetail?.accessIp || '')})
                    </div>
                    <div className="blogDetailMetaInfo">
                      <div>
                        작성일 :&nbsp;
                        {blogDetail?.regDate
                          ? commonUtils.formatDate(blogDetail.regDate, true)
                          : ''}
                      </div>
                      <div className="blogDetailMetaDivider" />
                      <div>조회 : {blogDetail?.readngCnt}</div>
                    </div>
                  </div>

                  <div className="blogDetailAction">
                    <div onClick={scrollToComment}>댓글 [{commentListCnt}]</div>
                    <div className="blogDetailMetaDivider" />
                    <div onClick={handleCopy}>URL복사</div>
                  </div>
                </div>
              </div>

              <div className="blogDetailDivider" />

              <div className="blogDetailViewer">
                <Viewer
                  key={blogDetail.nttSn}
                  initialValue={blogDetail.nttCn}
                />
              </div>
            </div>
          ) : (
            <div className="mobile-view">
              <div className="mobile-post-detail-container">
                <div className="mobile-post-detail-header">
                  <h1
                    className="mobile-post-detail-title"
                    onClick={() => {
                      navigate(`/inquiry`)
                    }}
                  >
                    소통
                  </h1>
                  <button className="mobile-url-copy-btn" onClick={handleCopy}>
                    URL복사
                  </button>
                </div>
                <div className="mobile-post-card" key={blogDetail?.nttSn}>
                  <div className="mobile-post-card-header">
                    <span
                      className={getMobileBadgeClass(blogDetail?.bbsSj || '00')}
                    >
                      {getBadgeLabel(blogDetail?.bbsSj ?? '기타')}
                    </span>
                    <h2 className="mobile-post-card-title">
                      {blogDetail?.nttSj}
                    </h2>
                    <div className="mobile-post-card-meta">
                      <div className="mobile-post-card-meta-row">
                        <span>
                          {withDrawYn
                            ? withDrawnCumtomer.name
                            : blogDetail?.nickName}
                          &nbsp; ({maskIpSuffix(blogDetail?.accessIp || '')})
                        </span>
                        <span>views {blogDetail?.readngCnt}</span>
                      </div>
                      <div className="mobile-post-card-meta-row">
                        <span>
                          {blogDetail?.regDate
                            ? commonUtils.formatDate(blogDetail.regDate, true)
                            : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mobile-post-card-content">
                    {blogDetail?.withDrawYn === 'Y' ? (
                      withDrawnCumtomer.contents
                    ) : (
                      <Viewer
                        key={blogDetail.nttSn}
                        initialValue={blogDetail.nttCn}
                      />
                    )}
                  </div>
                </div>
                <Comments
                  withDraw={blogDetail?.withDrawYn}
                  commentListCnt={commentListCnt}
                  nttSn={nttSn}
                  commentList={commentList}
                  cstId={user?.cstId}
                  nickName={user?.nickName}
                  getCommentList={getCommentList}
                />
                <div className="mobile-post-actions">
                  <button
                    className="mobile-action-btn primary"
                    onClick={() => navigate(`/blog`, { state: location.state })}
                  >
                    목록으로
                  </button>
                  {(user?.userId === blogDetail?.regId ||
                    user?.cstId === blogDetail?.regId) && (
                    <>
                      <button
                        className="mobile-action-btn user"
                        onClick={() => handleModalAction('update')}
                      >
                        수정
                      </button>
                      <button
                        className="mobile-action-btn user"
                        onClick={() => handleModalAction('delete')}
                      >
                        삭제
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {!isMobile && (
            <>
              <div ref={commentRef} className="blogDetailCommentControl">
                <div className="blogDetailCommentButtons">
                  {isDisabled ? (
                    <>
                      {user?.cstId === blogDetail?.regId && (
                        <>
                          <Button
                            color="neverbe"
                            onClick={() => handleModalAction('update')}
                          >
                            수정
                          </Button>
                          <Button
                            color="neverbe"
                            onClick={() => handleModalAction('delete')}
                          >
                            삭제
                          </Button>
                        </>
                      )}
                      <Button
                        color="neverbe"
                        onClick={() =>
                          navigate(`/blog`, { state: location.state })
                        }
                      >
                        목록
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        color="neverbe"
                        onClick={() => handleModalAction('cancel')}
                      >
                        취소
                      </Button>
                      <Button
                        color="neverbe"
                        onClick={() =>
                          navigate(`/blog`, { state: location.state })
                        }
                      >
                        목록
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <Comments
                withDraw={blogDetail?.withDrawYn}
                commentListCnt={commentListCnt}
                nttSn={nttSn}
                commentList={commentList}
                cstId={user?.cstId}
                nickName={user?.nickName}
                getCommentList={getCommentList}
              />
            </>
          )}
        </>
      ) : (
        <UpdateBlog
          nttSn={nttSn}
          nttSj={blogDetail.nttSj}
          nttCn={blogDetail.nttCn}
          bbsSj={blogDetail.bbsSj}
          detailFileList={fileList}
          isMobile={isMobile}
        />
      )}
    </>
  )
}

export default DetailBlog
